import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';
import { Cpu, Building2, Brain, Palette, Globe, Library, Languages, GraduationCap, Timer } from 'lucide-react';
import SEO from '@/components/university/common/SEO';
import UniversityHeader from '@/components/university/layout/UniversityHeader'
import UniversityFooter from '@/components/university/layout/UniversityFooter';

// ─── Design tokens ────────────────────────────────────────────────────────────
const UNI_MIDNIGHT = '#06060c';
const UNI_DARK = '#0a0a14';
const UNI_GOLD = '#d4a843';
const UNI_GOLD_DIM = 'rgba(212,168,67,0.15)';
const UNI_INDIGO = '#0ea5e9'; // Updated to logo Sky Blue
const UNI_BORDER = 'rgba(255,255,255,0.07)';
const UNI_TEXT = 'rgba(255,255,255,0.88)';
const UNI_MUTED = 'rgba(255,255,255,0.45)';

const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1 } },
};
const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const } },
};
const fadeIn = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.5 } },
};

const ReligiousCross = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v18M7 8h10" />
    </svg>
);

const getColleges = (t: any) => [
    { id: 'tech', icon: <Cpu />, name: t('university.enrol.col1', 'Technology & AI'), count: 5 },
    { id: 'biz', icon: <Building2 />, name: t('university.enrol.col2', 'Business & Entrepreneurship'), count: 5 },
    { id: 'health', icon: <Brain />, name: t('university.enrol.col3', 'Health & Human Sciences'), count: 5 },
    { id: 'arts', icon: <Palette />, name: t('university.enrol.col4', 'Creative Arts & Media'), count: 4 },
    { id: 'sci', icon: <Globe />, name: t('university.enrol.col5', 'Science & Environment'), count: 4 },
    { id: 'hum', icon: <Library />, name: t('university.enrol.col6', 'Humanities & Philosophy'), count: 5 },
    { id: 'lang', icon: <Languages />, name: t('university.enrol.col7', 'Languages & Communication'), count: 4 },
    { id: 'edu', icon: <GraduationCap />, name: t('university.enrol.col8', 'Education & Coaching'), count: 4 },
    { id: 'faith', icon: <ReligiousCross />, name: t('university.enrol.col9', 'Faith & Spiritual Studies'), count: 5 },
    { id: 'mastery', icon: <Timer />, name: t('university.enrol.col10', 'Personal Mastery'), count: 4 },
    { id: 'prep', icon: <GraduationCap />, name: t('university.enrol.col11', 'University Prep'), count: 4 },
];

const getPlans = (t: any) => [
    {
        key: 'university_monthly',
        label: t('university.enrol.planMonthly', 'Unlimited Monthly'),
        price: '$29',
        period: t('university.enrol.planMonthlyPeriod', '/month'),
        features: [
            t('university.enrol.monthlyF1', 'All 45 degrees'),
            t('university.enrol.monthlyF2', 'All modules & lessons'),
            t('university.enrol.monthlyF3', 'Degree certificates'),
            t('university.enrol.monthlyF4', 'Honours credentials')
        ],
        cta: t('university.enrol.ctaMonthly', 'Start Monthly'),
        highlight: true,
    },
    {
        key: 'university_annual',
        label: t('university.enrol.planAnnual', 'Unlimited Annual'),
        price: '$249',
        period: t('university.enrol.planAnnualPeriod', '/year'),
        features: [
            t('university.enrol.annualF1', 'All 45 degrees'),
            t('university.enrol.annualF2', 'All certificates & Honours'),
            t('university.enrol.annualF3', 'Priority new degrees'),
            t('university.enrol.annualF4', 'Save $99/year')
        ],
        cta: t('university.enrol.ctaAnnual', 'Start Annual'),
        highlight: false,
    },
];

function Section({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: '-80px' });
    return (
        <motion.section
            ref={ref}
            variants={stagger}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            style={style}
        >
            {children}
        </motion.section>
    );
}

export default function UniversityLandingPage() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 820);
        check();
        window.addEventListener('resize', check);
        return () => window.removeEventListener('resize', check);
    }, []);

    // Load Playfair Display from Google Fonts
    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@400;500;600;700&display=swap';
        document.head.appendChild(link);
        return () => { document.head.removeChild(link); };
    }, []);

    return (
        <>
            <UniversityHeader />
            <div style={{
                background: UNI_MIDNIGHT,
                minHeight: '100vh',
                color: UNI_TEXT,
                fontFamily: "'Inter', system-ui, sans-serif",
                overflowX: 'hidden',
            }}>
                <SEO
                    title="AI-Powered Conversational Education"
                    description="Experience the future of education with Still Waters University. 45 university-level degrees taught conversationally by AI. Start your journey for as little as $29."
                />

                {/* ── HERO ─────────────────────────────────────────────────────────── */}
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                    {/* Ambient gradient blobs */}
                    <div style={{
                        position: 'absolute', top: -200, left: '20%',
                        width: 600, height: 600, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(14,165,233,0.12) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }} />
                    <div style={{
                        position: 'absolute', top: -100, right: '10%',
                        width: 400, height: 400, borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(212,168,67,0.08) 0%, transparent 70%)',
                        pointerEvents: 'none',
                    }} />

                    <Section style={{
                        maxWidth: 900,
                        margin: '0 auto',
                        padding: isMobile ? '80px 20px 60px' : '120px 24px 80px',
                        textAlign: 'center'
                    }}>
                        {/* Eyebrow badge */}
                        <motion.div variants={fadeIn} style={{ marginBottom: 28 }}>
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 8,
                                background: UNI_GOLD_DIM, border: `1px solid rgba(212,168,67,0.3)`,
                                borderRadius: 100, padding: '6px 18px',
                                fontSize: 13, fontWeight: 600, color: UNI_GOLD, letterSpacing: '0.5px',
                            }}>
                                🎓 {t('university.landing.badge', 'Still Waters University — Now Open')}
                            </span>
                        </motion.div>

                        {/* Main headline */}
                        <motion.h1 variants={fadeUp} style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: isMobile ? '40px' : 'clamp(42px, 7vw, 80px)',
                            fontWeight: 900,
                            lineHeight: 1.1,
                            letterSpacing: '-1.5px',
                            margin: '0 0 24px',
                            color: '#fff',
                        }}>
                            {t('university.landing.headline', 'The Education\nYou Actually Need')}
                        </motion.h1>

                        <motion.p variants={fadeUp} style={{
                            fontSize: isMobile ? '16px' : 'clamp(16px, 2.5vw, 20px)',
                            color: UNI_MUTED,
                            maxWidth: 640,
                            margin: '0 auto 40px',
                            lineHeight: 1.7,
                            padding: isMobile ? '0 10px' : 0
                        }}>
                            {t('university.landing.subheadline', '45 university-level degrees. Taught conversationally by AI. From $29.')}
                        </motion.p>

                        {/* CTA buttons */}
                        <motion.div variants={fadeUp} style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: 16,
                            justifyContent: 'center',
                            alignItems: 'center'
                        }}>
                            <motion.button
                                whileHover={{ scale: 1.04, boxShadow: `0 8px 32px rgba(212,168,67,0.35)` }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => navigate('/university/enrol')}
                                style={{
                                    background: `linear-gradient(135deg, ${UNI_GOLD} 0%, #b8912e 100%)`,
                                    color: '#06060c',
                                    border: 'none', borderRadius: 14,
                                    padding: '18px 36px',
                                    fontSize: 16, fontWeight: 800,
                                    cursor: 'pointer',
                                    boxShadow: '0 4px 20px rgba(212,168,67,0.25)',
                                    width: isMobile ? '100%' : 'auto'
                                }}
                            >
                                {t('university.landing.ctaEnrol', 'Browse Degrees →')}
                            </motion.button>
                            <motion.button
                                whileHover={{ scale: 1.02, borderColor: 'rgba(255,255,255,0.3)' }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => navigate('/prep')}
                                style={{
                                    background: 'transparent',
                                    color: UNI_TEXT, border: `1.5px solid ${UNI_BORDER}`,
                                    borderRadius: 14, padding: '18px 36px',
                                    fontSize: 16, fontWeight: 600,
                                    cursor: 'pointer', transition: 'border-color 0.2s',
                                    width: isMobile ? '100%' : 'auto'
                                }}
                            >
                                {t('university.prep.startBtn', 'Start Free (Prep College)')}
                            </motion.button>
                        </motion.div>

                        {/* Social proof strip */}
                        <motion.div variants={fadeIn} style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: isMobile ? 24 : 40,
                            marginTop: 64,
                            maxWidth: 800,
                            margin: '64px auto 0'
                        }}>
                            {[
                                { stat: '45', label: t('university.landing.statProgrammes', 'Programmes') },
                                { stat: '200+', label: t('university.landing.statLessons', 'Lessons') },
                                { stat: '11', label: t('university.landing.statColleges', 'Colleges') },
                                { stat: '$49/mo', label: t('university.landing.statUnlimited', 'Unlimited') },
                            ].map(({ stat, label }) => (
                                <div key={stat} style={{ textAlign: 'center' }}>
                                    <div style={{
                                        fontFamily: "'Playfair Display', serif",
                                        fontSize: isMobile ? 28 : 36, fontWeight: 800, color: UNI_GOLD, letterSpacing: '-0.5px',
                                    }}>{stat}</div>
                                    <div style={{ fontSize: 11, color: UNI_MUTED, marginTop: 4, textTransform: 'uppercase', fontWeight: 600, letterSpacing: '0.5px' }}>{label}</div>
                                </div>
                            ))}
                        </motion.div>
                    </Section>
                </div>

                {/* ── COLLEGES GRID ────────────────────────────────────────────────── */}
                <Section style={{
                    maxWidth: 1200, margin: '0 auto',
                    padding: '80px 24px',
                }}>
                    <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
                        <p style={{ fontSize: 12, letterSpacing: '2px', color: UNI_GOLD, fontWeight: 600, marginBottom: 12 }}>
                            {t('university.landing.collegesEyebrow', '11 COLLEGES OF STUDY')}
                        </p>
                        <h2 style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: 'clamp(28px, 4vw, 44px)',
                            fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.5px',
                        }}>
                            {t('university.landing.collegesHeadline', 'Every field. One platform.')}
                        </h2>
                    </motion.div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                        gap: 16,
                    }}>
                        {getColleges(t).map((college, i) => (
                            <motion.div
                                key={college.name}
                                variants={fadeUp}
                                custom={i}
                                whileHover={{ y: -4, borderColor: 'rgba(212,168,67,0.4)', background: 'rgba(255,255,255,0.05)' }}
                                onClick={() => college.id === 'prep' ? navigate('/prep') : navigate(`/university/enrol?college=${college.id}`)}
                                style={{
                                    background: 'rgba(255,255,255,0.03)',
                                    border: `1px solid ${UNI_BORDER}`,
                                    borderRadius: 18,
                                    padding: '28px 24px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                }}
                            >
                                <div style={{ color: UNI_INDIGO, marginBottom: 14 }}>{college.icon}</div>
                                <div style={{ fontWeight: 600, fontSize: 15, color: '#fff', marginBottom: 6, lineHeight: 1.3 }}>
                                    {college.name}
                                </div>
                                <div style={{ fontSize: 13, color: UNI_MUTED }}>
                                    {college.count} {t('university.landing.degrees', 'degrees')}
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </Section>

                {/* ── HOW IT WORKS ─────────────────────────────────────────────────── */}
                <div style={{ background: UNI_DARK, borderTop: `1px solid ${UNI_BORDER}`, borderBottom: `1px solid ${UNI_BORDER}` }}>
                    <Section style={{ maxWidth: 1000, margin: '0 auto', padding: '80px 24px' }}>
                        <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
                            <p style={{ fontSize: 12, letterSpacing: '2px', color: UNI_INDIGO, fontWeight: 600, marginBottom: 12 }}>
                                {t('university.landing.howEyebrow', 'HOW IT WORKS')}
                            </p>
                            <h2 style={{
                                fontFamily: "'Playfair Display', serif",
                                fontSize: 'clamp(26px, 4vw, 40px)',
                                fontWeight: 700, color: '#fff', margin: 0,
                            }}>
                                {t('university.landing.howHeadline', 'University quality. None of the friction.')}
                            </h2>
                        </motion.div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 32 }}>
                            {[
                                {
                                    step: '01', icon: '🎯',
                                    title: t('university.landing.step1Title', 'Choose your degree'),
                                    desc: t('university.landing.step1Desc', 'Pick from 44 university-level programmes across 10 colleges. Enrol in one, or unlock all with Unlimited.'),
                                },
                                {
                                    step: '02', icon: '💬',
                                    title: t('university.landing.step2Title', 'Learn by conversation'),
                                    desc: t('university.landing.step2Desc', 'Your Still Waters professor teaches each lesson through dialogue — asking questions, giving feedback, correcting gaps in real time.'),
                                },
                                {
                                    step: '03', icon: '🏆',
                                    title: t('university.landing.step3Title', 'Earn your certificate'),
                                    desc: t('university.landing.step3Desc', 'Pass each module assessment (80%+) to earn a Still Waters Certificate. Complete all modules to earn your full degree credential.'),
                                },
                            ].map((item) => (
                                <motion.div key={item.step} variants={fadeUp} style={{ position: 'relative' }}>
                                    <div style={{
                                        fontSize: 11, fontWeight: 700, color: UNI_INDIGO,
                                        letterSpacing: '1.5px', marginBottom: 16,
                                    }}>
                                        STEP {item.step}
                                    </div>
                                    <div style={{ fontSize: 32, marginBottom: 14 }}>{item.icon}</div>
                                    <h3 style={{ fontWeight: 700, fontSize: 18, color: '#fff', marginBottom: 10 }}>{item.title}</h3>
                                    <p style={{ fontSize: 14, color: UNI_MUTED, lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                                </motion.div>
                            ))}
                        </div>
                    </Section>
                </div>

                {/* ── PRICING ──────────────────────────────────────────────────────── */}
                <Section style={{ maxWidth: 1100, margin: '0 auto', padding: '80px 24px' }}>
                    <motion.div variants={fadeUp} style={{ textAlign: 'center', marginBottom: 56 }}>
                        <p style={{ fontSize: 12, letterSpacing: '2px', color: UNI_GOLD, fontWeight: 600, marginBottom: 12 }}>
                            {t('university.landing.pricingEyebrow', 'PRICING')}
                        </p>
                        <h2 style={{
                            fontFamily: "'Playfair Display', serif",
                            fontSize: 'clamp(26px, 4vw, 40px)',
                            fontWeight: 700, color: '#fff', margin: '0 0 12px',
                        }}>
                            {t('university.landing.pricingHeadline', 'Less than one textbook.')}
                        </h2>
                        <p style={{ color: UNI_MUTED, fontSize: 16, margin: 0 }}>
                            {t('university.landing.pricingSub', 'Traditional degrees cost $30,000+. Still Waters University starts at $29.')}
                        </p>
                    </motion.div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20 }}>
                        {getPlans(t).map((plan) => (
                            <motion.div
                                key={plan.key}
                                variants={fadeUp}
                                whileHover={{ y: -4 }}
                                style={{
                                    background: plan.highlight
                                        ? `linear-gradient(160deg, rgba(14,165,233,0.15) 0%, rgba(212,168,67,0.08) 100%)`
                                        : 'rgba(255,255,255,0.03)',
                                    border: plan.highlight
                                        ? `1.5px solid rgba(14,165,233,0.5)`
                                        : `1px solid ${UNI_BORDER}`,
                                    borderRadius: 20,
                                    padding: '36px 32px',
                                    position: 'relative',
                                    transition: 'transform 0.2s',
                                }}
                            >
                                {plan.highlight && (
                                    <div style={{
                                        position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
                                        background: UNI_INDIGO, color: '#fff',
                                        padding: '4px 18px', borderRadius: 100, fontSize: 12, fontWeight: 700,
                                    }}>
                                        {t('university.landing.mostPopular', 'MOST POPULAR')}
                                    </div>
                                )}

                                <div style={{ fontSize: 14, fontWeight: 600, color: UNI_MUTED, marginBottom: 8 }}>
                                    {t(plan.label)}
                                </div>
                                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
                                    <span style={{
                                        fontFamily: "'Playfair Display', serif",
                                        fontSize: 48, fontWeight: 700, color: '#fff', letterSpacing: '-1px',
                                    }}>{plan.price}</span>
                                    <span style={{ color: UNI_MUTED, fontSize: 14 }}>{t(plan.period)}</span>
                                </div>

                                <div style={{ margin: '24px 0', borderTop: `1px solid ${UNI_BORDER}` }} />

                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 32 }}>
                                    {plan.features.map((f) => (
                                        <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                            <span style={{ color: UNI_GOLD, flexShrink: 0, marginTop: 1 }}>✓</span>
                                            <span style={{ fontSize: 14, color: UNI_MUTED, lineHeight: 1.5 }}>{t(f)}</span>
                                        </div>
                                    ))}
                                </div>

                                <motion.button
                                    whileHover={{ scale: 1.03 }}
                                    whileTap={{ scale: 0.97 }}
                                    onClick={() => navigate(`/university/enrol?plan=${plan.key}`)}
                                    style={{
                                        width: '100%',
                                        background: plan.highlight
                                            ? `linear-gradient(135deg, ${UNI_GOLD} 0%, #b8912e 100%)`
                                            : 'transparent',
                                        color: plan.highlight ? '#06060c' : UNI_TEXT,
                                        border: plan.highlight ? 'none' : `1.5px solid ${UNI_BORDER}`,
                                        borderRadius: 12, padding: '14px',
                                        fontSize: 15, fontWeight: 700, cursor: 'pointer',
                                        boxShadow: plan.highlight ? '0 4px 20px rgba(212,168,67,0.25)' : 'none',
                                    }}
                                >
                                    {t(plan.cta)} →
                                </motion.button>
                            </motion.div>
                        ))}
                    </div>

                    {/* Corporate link */}
                    <motion.div variants={fadeIn} style={{ textAlign: 'center', marginTop: 40 }}>
                        <p style={{ color: UNI_MUTED, fontSize: 14 }}>
                            {t('university.landing.corporateHint', 'Enrolling a team?')}{' '}
                            <button
                                onClick={() => navigate('/university/corporate')}
                                style={{
                                    background: 'none', border: 'none', color: UNI_GOLD,
                                    cursor: 'pointer', fontSize: 14, fontWeight: 600, textDecoration: 'underline',
                                }}
                            >
                                {t('university.landing.corporateLink', 'See corporate plans from $39/seat →')}
                            </button>
                        </p>
                    </motion.div>
                </Section>

                {/* ── FINAL CTA BANNER ─────────────────────────────────────────────── */}
                <div style={{
                    background: `linear-gradient(135deg, rgba(14,165,233,0.15) 0%, rgba(212,168,67,0.1) 100%)`,
                    borderTop: `1px solid ${UNI_BORDER}`,
                }}>
                    <Section style={{ maxWidth: 700, margin: '0 auto', padding: '80px 24px', textAlign: 'center' }}>
                        <motion.div variants={fadeUp}>
                            <h2 style={{
                                fontFamily: "'Playfair Display', serif",
                                fontSize: 'clamp(28px, 5vw, 52px)',
                                fontWeight: 700, color: '#fff',
                                margin: '0 0 20px', letterSpacing: '-0.5px',
                            }}>
                                {t('university.landing.finalHeadline', 'Your degree starts today.')}
                            </h2>
                            <p style={{ color: UNI_MUTED, fontSize: 16, lineHeight: 1.7, marginBottom: 40 }}>
                                {t('university.landing.finalSub', 'No application. No waiting list. No student debt. Just you, Still Waters University, and the education you\'ve been putting off.')}
                            </p>
                            <motion.button
                                whileHover={{ scale: 1.05, boxShadow: '0 12px 40px rgba(212,168,67,0.4)' }}
                                whileTap={{ scale: 0.97 }}
                                onClick={() => navigate('/university/enrol')}
                                style={{
                                    background: `linear-gradient(135deg, ${UNI_GOLD} 0%, #b8912e 100%)`,
                                    color: '#06060c', border: 'none',
                                    borderRadius: 16, padding: '18px 52px',
                                    fontSize: 17, fontWeight: 700, cursor: 'pointer',
                                    boxShadow: '0 4px 24px rgba(212,168,67,0.3)',
                                }}
                            >
                                {t('university.landing.finalCta', 'Enrol Now')} →
                            </motion.button>
                        </motion.div>
                    </Section>
                </div>
                <UniversityFooter />
            </div >
        </>
    );
}
